import type { Kind, SectionId } from '@/domain/types';

/**
 * Red's Bar and Grill's lists, in the order printed on the count sheets the bar uses now
 * (photographed week of Oct 12, 2026). A new tablet starts with these; a manager can rename,
 * add, remove and reorder them in the manager area.
 *
 * Sources:
 * - Names and order: the printed "Beer Count Close" and "Closing Liquor" sheets.
 * - Column B (empty bottle weight, oz): the red numbers on the printed liquor sheet. On the
 *   printout that column sits one row above the names from the top of the table (two drinks
 *   are squeezed into the header rows), so the numbers are matched by position: the 1st number
 *   to the 1st name, and so on. That gives weights that fit the bottles (e.g. Long Island's
 *   plastic jug 1.7, Crown Royal 24.65) where matching by printed row doesn't (Capt Dark 1.7).
 * - Beer Order pars and case sizes: the workbook file "NEW LIQUOR COUNTS TEMPLATE.xls".
 * - The row with no name between Triple Sec and Grand Marnier is kept blank on purpose
 *   (Carter, 2026-10-22); a manager can turn it into a drink.
 * Usual front-cooler amounts and prices are not set here: a manager sets them.
 */

export interface StarterItem {
  name: string;
  kind: Kind;
  rows: Partial<Record<SectionId, number>>;
  colB?: number;
  blankRow?: boolean;
  caseSize?: number;
  parCases?: number;
}

const BACK_BEER = [
  'GW lt',
  'GW Pil',
  'GW Classic',
  'Michelob Ultra',
  'O16',
  'Kokanee',
  'Coors Light',
  'Canadian',
  'Pilsner',
  'Bud Light',
  'Budweiser',
  'Boh',
  'Blackfly Gin Fizz',
  'Corona',
  'Kronenburg',
  'Stella',
  'Moosehead',
  'Truly',
  'Mikes Hard',
  'Rebellion Zilla IPA',
  'Smirnoff Ice',
  'Twisted Tea',
  'Coors Seltzers',
  'Guiness',
  'Corona 0 (Sunbrew)',
  "GW 306's",
  'Lakelife',
  'Homebound',
];

/** The second beer list (front bar cooler). Same drinks; Truly and Blackfly sit higher up. */
const FRONT_BEER = [
  'GW lt',
  'GW Pil',
  'GW Classic',
  'Michelob Ultra',
  'O16',
  'Kokanee',
  'Coors Light',
  'Canadian',
  'Pilsner',
  'Bud Light',
  'Budweiser',
  'Boh',
  'Truly',
  'Corona',
  'Kronenburg',
  'Stella',
  'Moosehead',
  'Blackfly Gin Fizz',
  'Mikes Hard',
  'Rebellion Zilla IPA',
  'Smirnoff Ice',
  'Twisted Tea',
  'Coors Seltzers',
  'Guiness',
  'Corona 0 (Sunbrew)',
  "GW 306's",
  'Lakelife',
  'Homebound',
];

/** Beer Order sheet: par in cases × bottles per case (from the workbook). */
const ORDER: Record<string, [parCases: number, caseSize: number]> = {
  'GW lt': [10, 12],
  'GW Pil': [10, 12],
  'Michelob Ultra': [6, 12],
  O16: [16, 12],
  Kokanee: [4, 24],
  'Coors Light': [5, 24],
  Canadian: [4, 24],
  Pilsner: [4, 24],
  'Bud Light': [10, 24],
  Budweiser: [4, 24],
  Boh: [5, 12],
  Corona: [6, 24],
  Stella: [2, 12],
  Moosehead: [12, 12],
};

const LIQUOR = [
  'Jim Beam Peach',
  'Canadian Club Apple',
  'Canadian Club',
  'Crown Royal',
  'Dock 57',
  'Gibsons',
  'Gin - Bombay',
  'Jack Daniels',
  'Jagermeister',
  'Jameson',
  'Honey Jack',
  'Jim Beam Red Stag',
  'Jim Beam',
  'Long Island',
  'Rum - Capt Dark',
  'Rum - Capt Spiced',
  'Smirnoff Raspberry',
  'Rum - Malibu',
  'Southern Comfort',
  'Sauza - Silver',
  'Hornitos',
  '1800 Tequila',
  'Vodka Whipped',
  "Wiser's Whiskey",
  'FireJack',
  'Vodka - Grey Goose',
  'Kettle One',
  'Vodka - Pinnacle',
  'Bowmore',
  'Glenfiddich',
  'Patron',
  'Knob Creek',
  'Auchentoshan',
];

const LIQUEUR = [
  'Amaretto',
  'Apple Pie',
  'Baileys',
  'Bols - Banana',
  'Blue Curacao',
  'Bols - Melon',
  'Butterscotch',
  'Root Beer Schnapps',
  'Cherry Whisky',
  'Cacao White',
  'Peppermint',
  'Fireball',
  'Frangelico',
  'Galliano',
  'White Sambuca',
  'Peach Schnapps',
  'Sambuca-Black',
  'Sourpuss Apple',
  'Sourpuss Raspberry',
  'Hennessy',
  'Triple Sec',
  '',
  'Grand Marnier',
  'Blueberry Vodka',
  'Kahlua',
  'PB Tequila',
  'Safe Liquor',
];

/** Empty bottle weights (oz) from column B of the printed liquor sheet, in list order. */
const LIQUOR_EMPTY = [16.1, 15, 13.65, 24.65, 14.55, 16.75, 20.45, 23.65, 28.7, 17.05, 17.1, 17, 15.05, 1.7, 19.75, 20, 16.25, 23.35, 17.25, 27.9, 23.5, 26.9, 18.7, 18.45, 17.65, 19.95, 21.9, 16.7, 20, 18.45, 24, 22.85, 22.2];
const LIQUEUR_EMPTY = [23.8, 21.45, 30, 17.6, 17.05, 16.55, 23.3, 21.35, 23.7, 24.3, 22.65, 4.2, 27.4, 16.65, 16.9, 21.95, 16.9, 21.8, 22.1, 18.7, 23, 24, 23.2, 16.25, 28.25, 21, 0];

export function redsStarterItems(): StarterItem[] {
  const beers = new Map<string, StarterItem>();
  BACK_BEER.forEach((name, i) => beers.set(name, { name, kind: 'beer', rows: { back_beer: i + 1 } }));
  FRONT_BEER.forEach((name, i) => {
    const it = beers.get(name) ?? { name, kind: 'beer' as const, rows: {} };
    it.rows.front_beer = i + 1;
    beers.set(name, it);
  });
  for (const [name, [parCases, caseSize]] of Object.entries(ORDER)) {
    const it = beers.get(name);
    if (it) Object.assign(it, { parCases, caseSize });
  }
  const liquor: StarterItem[] = [
    ...LIQUOR.map((name, i) => ({ name, kind: 'liquor' as const, rows: { liquor: i + 1 }, colB: LIQUOR_EMPTY[i]! })),
    ...LIQUEUR.map((name, i) => ({ name, kind: 'liquor' as const, rows: { liqueur: i + 1 }, colB: LIQUEUR_EMPTY[i]!, ...(name === '' ? { blankRow: true } : {}) })),
  ];
  return [...beers.values(), ...liquor];
}
